import React, { useState } from 'react';
import { TournamentEventData } from '../../../types/EventTypes';

interface EventStepTournamentProps {
  initialData?: TournamentEventData;
  onNext: (data: TournamentEventData) => void;
  onBack: () => void;
}

// ✅ Type guard for valid difficulty values
const isValidDifficulty = (value: any): value is 'easy' | 'medium' | 'hard' => {
  return ['easy', 'medium', 'hard'].includes(value);
};

const EventStepTournament: React.FC<EventStepTournamentProps> = ({
  initialData,
  onNext,
  onBack,
}) => {
  const [tournamentName, setTournamentName] = useState<string>(
    initialData?.tournamentName || ''
  );
  const [groupStageInfo, setGroupStageInfo] = useState<string>(
    initialData?.groupStageInfo || ''
  );

  const defaultMatch = {
    opponent: '',
    startTime: '',
    difficulty: 'easy' as 'easy',
    referee: { name: '', email: '', phone: '' },
  };

  const [matches, setMatches] = useState<TournamentEventData['matches']>(
    initialData?.matches?.length
      ? initialData.matches.map(match => ({
          opponent: match.opponent || '',
          startTime: match.startTime || '',
          difficulty: isValidDifficulty(match.difficulty)
            ? match.difficulty
            : 'easy',
          referee: {
            name: match.referee?.name || '',
            email: match.referee?.email || '',
            phone: match.referee?.phone || '',
          },
        }))
      : [{ ...defaultMatch }]
  );

  const [errors, setErrors] = useState<{
    tournamentName?: string;
    matches?: Record<number, { opponent?: string; startTime?: string }>;
  }>({});

  const handleMatchChange = (
    index: number,
    field: 'opponent' | 'startTime' | 'difficulty',
    value: string
  ) => {
    setMatches(prev =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const handleRefereeChange = (
    index: number,
    field: 'name' | 'email' | 'phone',
    value: string
  ) => {
    setMatches(prev =>
      prev.map((m, i) =>
        i === index ? { ...m, referee: { ...m.referee, [field]: value } } : m
      )
    );
  };

  const addMatch = () => {
    setMatches(prev => [...prev, { ...defaultMatch }]);
  };

  const removeMatch = (index: number) => {
    setMatches(prev => prev.filter((_, i) => i !== index));
  };

  const handleNextClick = () => {
    const newErrors: typeof errors = {};
    if (!tournamentName.trim()) {
      newErrors.tournamentName = 'Tournament name is required.';
    }
    if (matches.length === 0) {
      newErrors.matches = {
        0: {
          opponent: 'At least one match is required.',
          startTime: 'At least one match is required.',
        },
      };
    } else {
      matches.forEach((match, idx) => {
        if (!match.opponent.trim()) {
          newErrors.matches = newErrors.matches || {};
          newErrors.matches[idx] = {
            ...(newErrors.matches[idx] || {}),
            opponent: 'Opponent is required.',
          };
        }
        if (!match.startTime) {
          newErrors.matches = newErrors.matches || {};
          newErrors.matches[idx] = {
            ...(newErrors.matches[idx] || {}),
            startTime: 'Start time is required.',
          };
        }
      });
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const tournamentData: TournamentEventData = {
      tournamentName: tournamentName.trim(),
      groupStageInfo: groupStageInfo.trim(),
      matches: matches.map(match => {
        const baseMatch = {
          opponent: match.opponent.trim(),
          startTime: match.startTime,
          difficulty: match.difficulty,
        } as TournamentEventData['matches'][number];

        if (
          match.referee?.name ||
          match.referee?.email ||
          match.referee?.phone
        ) {
          baseMatch.referee = {
            name: match.referee.name || '',
            email: match.referee.email || '',
            phone: match.referee.phone || '',
          };
        }

        return baseMatch;
      }),
    };

    onNext(tournamentData);
  };

  return (
    <div className='tournament-step'>
      <h2 className='text-xl font-bold mb-4'>Tournament Details</h2>

      <div className='form-field mb-4'>
        <label className='block font-medium mb-1'>
          Tournament Name <span className='text-red-500'>*</span>
        </label>
        <input
          type='text'
          className='p-2 border rounded w-full'
          value={tournamentName}
          onChange={e => setTournamentName(e.target.value)}
        />
        {errors.tournamentName && (
          <div className='text-red-600 text-sm mt-1'>
            {errors.tournamentName}
          </div>
        )}
      </div>

      <div className='form-field mb-6'>
        <label className='block font-medium mb-1'>
          Group Stage Info (optional)
        </label>
        <textarea
          className='p-2 border rounded w-full'
          value={groupStageInfo}
          onChange={e => setGroupStageInfo(e.target.value)}
          rows={3}
        />
      </div>

      <h3 className='text-lg font-semibold mb-2'>Matches</h3>
      {matches.map((match, idx) => (
        <div key={idx} className='border rounded p-4 mb-4 bg-gray-50 space-y-3'>
          <div>
            <label className='block text-sm font-medium'>
              Opponent <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={match.opponent}
              onChange={e => handleMatchChange(idx, 'opponent', e.target.value)}
              className='p-2 border rounded w-full'
            />
            {errors.matches?.[idx]?.opponent && (
              <div className='text-red-600 text-sm mt-1'>
                {errors.matches[idx].opponent}
              </div>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium'>
              Start Time <span className='text-red-500'>*</span>
            </label>
            <input
              type='time'
              value={match.startTime}
              onChange={e =>
                handleMatchChange(idx, 'startTime', e.target.value)
              }
              className='p-2 border rounded w-full'
            />
            {errors.matches?.[idx]?.startTime && (
              <div className='text-red-600 text-sm mt-1'>
                {errors.matches[idx].startTime}
              </div>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium'>Difficulty</label>
            <select
              value={match.difficulty}
              onChange={e =>
                handleMatchChange(idx, 'difficulty', e.target.value)
              }
              className='p-2 border rounded w-full'
            >
              <option value='easy'>Easy</option>
              <option value='medium'>Medium</option>
              <option value='hard'>Hard</option>
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium'>
              Referee Details (optional)
            </label>
            <input
              type='text'
              placeholder='Referee Name'
              value={match.referee?.name || ''}
              onChange={e => handleRefereeChange(idx, 'name', e.target.value)}
              className='p-2 border rounded w-full mb-2'
            />
            <input
              type='email'
              placeholder='Referee Email'
              value={match.referee?.email || ''}
              onChange={e => handleRefereeChange(idx, 'email', e.target.value)}
              className='p-2 border rounded w-full mb-2'
            />
            <input
              type='tel'
              placeholder='Referee Phone'
              value={match.referee?.phone || ''}
              onChange={e => handleRefereeChange(idx, 'phone', e.target.value)}
              className='p-2 border rounded w-full'
            />
          </div>

          {matches.length > 1 && (
            <button
              type='button'
              onClick={() => removeMatch(idx)}
              className='text-sm text-red-600 mt-2 underline'
            >
              Remove Match
            </button>
          )}
        </div>
      ))}

      <button
        type='button'
        onClick={addMatch}
        className='px-3 py-1 bg-blue-600 text-white rounded mb-6'
      >
        Add Another Match
      </button>

      <div className='flex justify-between'>
        <button
          type='button'
          onClick={onBack}
          className='px-4 py-2 bg-gray-300 rounded'
        >
          Back
        </button>
        <button
          type='button'
          onClick={handleNextClick}
          className='px-4 py-2 bg-green-600 text-white rounded'
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default EventStepTournament;
